import { useTranslation } from 'react-i18next'
import { Alert, type AlertTone } from './ui'

// daysUntil returns whole calendar days from now until `d` (negative = past).
export function daysUntil(d: Date): number {
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000)
}

// GrantBanner surfaces a time-limited admin grant (e.g. a comped "1 year free"
// paid plan) so the customer can see exactly when their access lapses — the
// transparency the plain plan pill never gave them. Tone escalates from brand
// (plenty of time) → amber (≤14 days) → red (expired).
export default function GrantBanner({ expiresAt }: { expiresAt: string }) {
  const { t, i18n } = useTranslation()
  const exp = new Date(expiresAt)
  const days = daysUntil(exp)
  const dateStr = exp.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })
  const expired = days < 0
  const urgent = days >= 0 && days <= 14

  const tone: AlertTone = expired ? 'danger' : urgent ? 'warning' : 'accent'

  return (
    <Alert tone={tone}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {expired
          ? t('billing.grantExpiredOn', { date: dateStr })
          : t('billing.grantEndsOn', { date: dateStr })}
      </div>
      {!expired && (
        <p className="text-xs mt-1 opacity-90">
          {days === 0 ? t('billing.grantEndsToday') : t('billing.grantDaysLeft', { count: days })}
        </p>
      )}
    </Alert>
  )
}
